import * as React from 'react';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getCatalogSellerId, getShouldShowLive } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import CatalogCard from '../CatalogCard/CatalogCard';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import useVisibilitySensor from '@rooks/use-visibility-sensor';

type Props = {
    cardClassname: string;
    catalogId: number;
    className?: string;
    position?: number;
};

const CatalogVisibilityWrapper = ({ cardClassname, catalogId, className, ...rest }: Props) => {
    const dispatch = useAppDispatch();
    const catalogRef = React.useRef<HTMLDivElement>(null);
    const [visibilityMessageSent, setVisibilityMessageSent] = React.useState(false);

    const { isVisible } = useVisibilitySensor(catalogRef, {
        intervalCheck: 750,
        resizeCheck: true,
        scrollCheck: true,
    });

    const previousIsVisible = usePrevious(isVisible);

    // Live catalog data selectors
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));

    const live = showLive || liveStatus === 'live';

    React.useEffect(() => {
        if (!previousIsVisible && isVisible && !visibilityMessageSent && live) {
            // Fetch live catalog status from auction-engine
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
            setVisibilityMessageSent(true);
        }
    }, [dispatch, isVisible, previousIsVisible, catalogId, visibilityMessageSent, live, sellerId]);

    return (
        <div
            className={className}
            ref={catalogRef}
        >
            <CatalogCard
                catalogId={catalogId}
                className={cardClassname}
                {...rest}
            />
        </div>
    );
};

export default CatalogVisibilityWrapper;
